// ============================================================
// SheetSections - the sections of the sheet, in order.
//
// Split in two so neither is a wall of markup: the fixed sections (what
// every combatant has: characteristics, vitals, armour, defence,
// movement) and the lists (traits, talents, skills), which own the
// picker that "+ ADD" opens.
// ============================================================
import { useState } from 'react';
import type { ReactNode } from 'react';
import { isObject, type BodyShape } from '../../engine/bodyShape';
import { getGrantedTalents } from '../../engine/character/effectiveTalents';
import type { CharacteristicId } from '../../engine/character/types';
import type { Pin, SheetSectionId } from '../../engine/roster/pins';
import ArmourBodySection, { armourSummary } from './ArmourBodySection';
import CharacteristicStrip from './CharacteristicStrip';
import DefenceSection, { defenceSummary } from './DefenceSection';
import InstancesSection, { AddButton } from './InstancesSection';
import type { InstanceKind } from './instanceKinds';
import MovementSection, { movementSummary } from './MovementSection';
import PinMark from './PinMark';
import SheetSection, { FOLDABLE, type useSheetFolds } from './SheetSection';
import SheetSwitch from './SheetSwitch';
import SkillRows, { skillSummary } from './SkillRows';
import VitalsSection, { type SectionProps as Halves } from './VitalsSection';
import EffectParts from '../effects/EffectParts';
import EffectsGlance, { InnateLine } from './EffectsGlance';
import { isExposureCode } from '../../engine/exposures';
import { readEffects } from '../../engine/character/effectItems';
import styles from './Sheet.module.css';

/** What every section needs from the host: the pins, the folds and the sheet lock. */
interface SectionContext {
  pins: Pin[];
  folds: ReturnType<typeof useSheetFolds>;
  sheetLocked: boolean;
  resetPin: (pin: Pin) => void;
  /** The sections a search found, or null when nothing is being searched for. */
  found: Set<SheetSectionId> | null;
}

interface SectionProps {
  ctx: SectionContext;
  id: SheetSectionId;
  title: string;
  summary?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}

/** One section of the sheet, with its pins under it and its fold remembered. */
function Section({ ctx, id, title, summary, action, children }: SectionProps) {
  // A search shows only the sections that hold what was typed, opened.
  if (ctx.found && !ctx.found.has(id)) return null;
  const mine = ctx.pins.filter((pin) => pin.section === id);
  const fold =
    FOLDABLE.has(id) && !ctx.found ? { folded: ctx.folds.isFolded(id), onToggle: () => ctx.folds.toggle(id) } : {};
  return (
    <SheetSection id={id} title={title} summary={summary} action={action} {...fold}>
      {children}
      {mine.length > 0 && (
        <div className={styles.pinRow}>
          {mine.map((pin) => (
            <PinMark key={`${pin.key}.${pin.leaf ?? ''}`} pin={pin} disabled={ctx.sheetLocked} onReset={ctx.resetPin} />
          ))}
        </div>
      )}
    </SheetSection>
  );
}

interface Props {
  ctx: SectionContext;
  /** The sheet, the projection and the writers every section takes. */
  halves: Halves;
}

/** An Object (2026-10-26) is a thing: its Toughness is the one characteristic it has. */
const OBJECT_CHARACTERISTICS: readonly CharacteristicId[] = ['tgh'];

/** Characteristics, vitals and armour: always open. */
export function CoreSections({
  ctx,
  halves,
  onRebody,
}: Props & { onRebody: (shape: BodyShape, crowd: boolean) => void }) {
  const { character, target, onChange, sheetLocked } = halves;
  return (
    <>
      <Section ctx={ctx} id="characteristics" title="Characteristics">
        <CharacteristicStrip
          character={character}
          onChange={onChange}
          disabled={sheetLocked}
          only={isObject(character) ? OBJECT_CHARACTERISTICS : undefined}
        />
        <div className={styles.sectionNote}>
          Under each value: the bonus in play, and the unnatural multiplier. Click a number to edit it.
        </div>
      </Section>
      <Section ctx={ctx} id="vitals" title="Vitals">
        <VitalsSection {...halves} />
      </Section>
      <Section ctx={ctx} id="armour" title="Armour" summary={armourSummary(target, character)}>
        <ArmourBodySection {...halves} onRebody={onRebody} />
      </Section>
    </>
  );
}

/** "2 Solo modes · 3 always on · 2 conditions", or what is missing: the Effects section folded. */
function effectsSummary(character: Halves['character'], conditions: readonly string[] | undefined): string {
  const solo = character.soloModes?.length ?? 0;
  const always = readEffects(character.permanentEffects).length + (character.alwaysOn?.length ?? 0);
  const now = (conditions ?? []).filter((c) => !isExposureCode(c)).length;
  return [
    count(solo, 'Solo mode'),
    ...(always ? [`${always} always on`] : []),
    ...(now ? [`${now} condition${now === 1 ? '' : 's'}`] : []),
  ].join(' · ');
}

const count = (n: number, noun: string) => (n === 0 ? `no ${noun}s` : String(n));
const talentSummary = (taken: number, granted: number) =>
  granted ? `${count(taken, 'talent')} · ${granted} granted` : count(taken, 'talent');

/** Xenos: a fact about the creature that no catalog trait carries. */
function XenosSwitch({ character, onChange, sheetLocked }: Halves) {
  return (
    <div className={styles.reaction}>
      <SheetSwitch
        label="Xenos"
        checked={!!character.xenos}
        disabled={sheetLocked}
        onChange={(xenos) => onChange({ xenos })}
      />
      {character.xenos && (
        <span className={styles.hint}>
          Attackers with Deathwatch Training auto-confirm Righteous Fury against this target.
        </span>
      )}
    </div>
  );
}

/** Traits, defence, movement, talents and skills. Owns which picker is open: one at a time. */
/**
 * EFFECTS (Squad Mode stage 4; plan modes-rework): at the table a glance - three lines, Edit unfolds the parts
 * (`EffectsGlance`); elsewhere the parts themselves (`EffectParts`: each bundle on its bench). Switched on the actor's
 * panel (ModeStrip); what he carries this moment is the Status tab's. The section keeps its id `stances`, so a table's
 * folds and pins on it survive the rename. Squad Mode and Now are the table's alone: the library editor has its own
 * Effects tab, and no Status tab to point at.
 */
function EffectsSection({ ctx, halves }: Props) {
  const { character, onChange, sheetLocked, atTable, combatantState } = halves;
  const parts = (
    <EffectParts
      character={character}
      onChange={onChange}
      locked={sheetLocked}
      atTable={atTable}
      state={combatantState}
      where={`${character.name} · ${atTable ? 'sheet' : 'my library'}`}
      alwaysExtra={atTable ? <InnateLine character={character} /> : undefined}
    />
  );
  return (
    <Section ctx={ctx} id="stances" title="Effects" summary={effectsSummary(character, combatantState?.conditions)}>
      {atTable ? (
        <EffectsGlance character={character} state={combatantState}>
          {parts}
        </EffectsGlance>
      ) : (
        parts
      )}
    </Section>
  );
}

/** How far it moves and how big it is; an Object, which never moves, keeps only its Size (board 7). */
function MoveSection({ ctx, halves }: Props) {
  const { character, onChange, sheetLocked } = halves;
  const thing = isObject(character);
  return (
    <Section
      ctx={ctx}
      id="movement"
      title={thing ? 'Size' : 'Movement and size'}
      summary={thing ? character.size : movementSummary(character)}
    >
      <MovementSection character={character} onChange={onChange} disabled={sheetLocked} />
    </Section>
  );
}

export function ListSections({ ctx, halves }: Props) {
  const { character, target, onChange, sheetLocked } = halves;
  const [picking, setPicking] = useState<InstanceKind | null>(null);
  /** One of the two chip sections: its props and the "+ ADD" that opens its picker. */
  const chips = (kind: InstanceKind) => ({
    props: {
      kind,
      character,
      onChange,
      disabled: sheetLocked,
      picking: picking === kind,
      onPicking: (open: boolean) => setPicking(open ? kind : null),
    },
    add: <AddButton kind={kind} disabled={sheetLocked} onClick={() => setPicking(picking === kind ? null : kind)} />,
  });
  const [traits, talents] = [chips('trait'), chips('talent')];
  const talentCount = talentSummary(character.talents.length, getGrantedTalents(character).length);
  // An Object keeps its traits, defence and size; it has no talents, skills, movement or effects (board 7).
  const person = !isObject(character);
  return (
    <>
      <Section
        ctx={ctx}
        id="traits"
        title="Traits"
        summary={count(character.traits.length, 'trait')}
        action={traits.add}
      >
        <InstancesSection {...traits.props}>
          <XenosSwitch {...halves} />
        </InstancesSection>
      </Section>
      <Section ctx={ctx} id="defence" title="Defence" summary={defenceSummary(target)}>
        <DefenceSection {...halves} />
      </Section>
      <MoveSection ctx={ctx} halves={halves} />
      {person && (
        <>
          <Section ctx={ctx} id="talents" title="Talents" summary={talentCount} action={talents.add}>
            <InstancesSection {...talents.props} />
          </Section>
          <Section ctx={ctx} id="skills" title="Skills" summary={skillSummary(character)}>
            <SkillRows character={character} onChange={onChange} disabled={sheetLocked} />
          </Section>
          <EffectsSection ctx={ctx} halves={halves} />
        </>
      )}
    </>
  );
}
